/**
 * see src/features/loans/xstate/types/LoanApplication.types.ts
 */

import { GetLoanApplicationsData, GetLoanData } from '@shared/api/lib/generated/loansPublicApi.generated';
import { Uuid, UuidPrefix } from '@shared/types/uuid';

import Big from 'big.js';
import { DateTime } from 'luxon';

export type LoanApplicationDto = GetLoanApplicationsData[number];

type MapLoanApplicationTypes<T> = {
  [P in keyof T]: P extends 'createdAt' | 'updatedAt'
    ? DateTime
    : P extends 'submittedAt'
    ? DateTime | null
    : P extends
        | 'borrowPrincipalAmount'
        | 'expectedMonthlyExpenses'
        | 'expectedMonthlyIncome'
        | 'expectedTotalExistingCreditLimits'
        | 'monthlyExpenses'
        | 'monthlyIncome'
        | 'securityAmount'
        | 'totalExistingCreditLimits'
    ? Big | null
    : T[P];
};

export type LoanApplication = MapLoanApplicationTypes<LoanApplicationDto>;

export const bigifyLoanApplication = (loanApplication: LoanApplicationDto): LoanApplication => ({
  ...loanApplication,
  borrowPrincipalAmount: loanApplication.borrowPrincipalAmount ? new Big(loanApplication.borrowPrincipalAmount) : null,
  createdAt: DateTime.fromMillis(loanApplication.createdAt),
  depositAddressUuid: loanApplication.depositAddressUuid as Uuid<UuidPrefix.DepositAddress> | null,
  expectedMonthlyExpenses: loanApplication.expectedMonthlyExpenses
    ? new Big(loanApplication.expectedMonthlyExpenses)
    : null,
  expectedMonthlyIncome: loanApplication.expectedMonthlyIncome ? new Big(loanApplication.expectedMonthlyIncome) : null,
  expectedTotalExistingCreditLimits: loanApplication.expectedTotalExistingCreditLimits
    ? new Big(loanApplication.expectedTotalExistingCreditLimits)
    : null,
  monthlyExpenses: loanApplication.monthlyExpenses ? new Big(loanApplication.monthlyExpenses) : null,
  monthlyIncome: loanApplication.monthlyIncome ? new Big(loanApplication.monthlyIncome) : null,
  securityAmount: loanApplication.securityAmount ? new Big(loanApplication.securityAmount) : null,
  submittedAt: loanApplication.submittedAt ? DateTime.fromMillis(loanApplication.submittedAt) : null,
  totalExistingCreditLimits: loanApplication.totalExistingCreditLimits
    ? new Big(loanApplication.totalExistingCreditLimits)
    : null,
  updatedAt: DateTime.fromMillis(loanApplication.updatedAt),
  userId: loanApplication.userId as Uuid<UuidPrefix.User>,
});

export type LoanDto = GetLoanData;

export type MapLoanTypes<T> = {
  [P in keyof T]: P extends 'createdAt' | 'validUntil'
    ? DateTime
    : P extends
        | 'attentionLimit'
        | 'borrowedFiatPrincipalAmount'
        | 'borrowPrincipalAmount'
        | 'defaultNoticeLimit'
        | 'fiatFeeRatio'
        | 'fiatLoanLimit'
        | 'fiatPrincipalAvailableToBorrow'
        | 'interestRateBps'
        | 'loanToValue'
        | 'loanToValue24hTrend'
        | 'maxBorrowLtv'
        | 'maxLimit'
        | 'pendingBorrowFiatPrincipalAmount'
        | 'pendingWithdrawSecurityCryptoAmount'
        | 'principalLeft'
        | 'principalRepaid'
        | 'repaymentNoticeLimit'
        | 'securityAmount'
        | 'securityCryptoAmount'
        | 'securityFiatAmount'
        | 'termInDays'
    ? Big | null
    : T[P];
};

export type Loan = MapLoanTypes<LoanDto>;

export const bigifyLoan = (loan: LoanDto): Loan => ({
  ...loan,
  borrowPrincipalAmount: loan.borrowPrincipalAmount ? new Big(loan.borrowPrincipalAmount) : null,
  createdAt: DateTime.fromMillis(loan.createdAt),
  validUntil: DateTime.fromMillis(loan.validUntil),
  securityAmount: loan.securityAmount ? new Big(loan.securityAmount) : null,
  securityCryptoAmount: loan.securityCryptoAmount ? new Big(loan.securityCryptoAmount) : null,
  securityFiatAmount: loan.securityFiatAmount ? new Big(loan.securityFiatAmount) : null,
  pendingWithdrawSecurityCryptoAmount: loan.pendingWithdrawSecurityCryptoAmount
    ? new Big(loan.pendingWithdrawSecurityCryptoAmount)
    : null,
  loanToValue: loan.loanToValue ? new Big(loan.loanToValue) : null,
  loanToValue24hTrend: loan.loanToValue24hTrend ? new Big(loan.loanToValue24hTrend) : null,
  borrowedFiatPrincipalAmount: loan.borrowedFiatPrincipalAmount ? new Big(loan.borrowedFiatPrincipalAmount) : null,
  pendingBorrowFiatPrincipalAmount: loan.pendingBorrowFiatPrincipalAmount
    ? new Big(loan.pendingBorrowFiatPrincipalAmount)
    : null,
  fiatFeeRatio: loan.fiatFeeRatio ? new Big(loan.fiatFeeRatio) : null,
  fiatPrincipalAvailableToBorrow: loan.fiatPrincipalAvailableToBorrow
    ? new Big(loan.fiatPrincipalAvailableToBorrow)
    : null,
  fiatLoanLimit: loan.fiatLoanLimit ? new Big(loan.fiatLoanLimit) : null,
  interestRateBps: loan.interestRateBps ? new Big(loan.interestRateBps) : null,
  termInDays: loan.termInDays ? new Big(loan.termInDays) : null,
  maxBorrowLtv: loan.maxBorrowLtv ? new Big(loan.maxBorrowLtv) : null,
  principalRepaid: loan.principalRepaid ? new Big(loan.principalRepaid) : null,
  principalLeft: loan.principalLeft ? new Big(loan.principalLeft) : null,
  defaultNoticeLimit: loan.defaultNoticeLimit ? new Big(loan.defaultNoticeLimit) : null,
  repaymentNoticeLimit: loan.repaymentNoticeLimit ? new Big(loan.repaymentNoticeLimit) : null,
  attentionLimit: loan.attentionLimit ? new Big(loan.attentionLimit) : null,
  maxLimit: loan.maxLimit ? new Big(loan.maxLimit) : null,
  loanId: loan.loanId as Uuid<UuidPrefix.Loan>,
  userId: loan.userId as Uuid<UuidPrefix.User>,
});
